import { useContext, useEffect, useMemo, useState } from 'react'

import {
  BMRCardEmbed,
  BMRCardEmbedProps,
  useHealthToolsCache,
  BmiForm,
  BmiFormValue,
  BmiFormProps,
} from '@hhgtech/hhg-components'
import { ToolContent } from '@hhgtech/hhg-components/build/together/interfaces/types'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import { format, subYears } from 'date-fns'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatBMIContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import * as BmiFormHelpers from 'components/molecules/bmiForm/index.helpers'
import {
  BMR_SIDIS_KEY,
  generateBmrInfoToSendSidis,
  generateBmrRedirect,
  getBmrKcal,
} from 'components/pages/healthTools/bmr/helpers'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { Category, HealthToolPositionType } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { HealthToolCardEmbedProps } from '../types'

type CommonProps = {
  articleCategories?: Category[] | null
  positionType: HealthToolPositionType
} & HealthToolCardEmbedProps

export type BMRCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<BmiFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<BMRCardEmbedProps> & CommonProps)

export const BMRCardWrapper = ({
  isSide,
  showUserResult: _showUserResult,
  positionType,
  articleCategories,
  onlyUseForm,
  ...props
}: BMRCardWrapperProps) => {
  const { formatMessage: f } = useIntl()
  const router = useRouter()
  const isMobile = useIsMobile()
  const [bmrContent, setBmrContent] = useState<ToolContent>({} as ToolContent)

  const { state } = useContext(AppContext) || {}

  const { isLoggedIn } = state || {}

  const { locale } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const bmrLink = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.BMR), [
    getHealthToolLink,
  ])
  const currentUrl = useCurrentUrl()

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_BMR_INFO)
      setBmrContent(formatBMIContent(res?.data) as ToolContent)
    })()
  }, [])

  const onSubmit = ({ gender = 'male', age, height, weight }: BmiFormValue) => {
    const info = { age, height, weight }
    const bmr = getBmrKcal(gender, info as any)
    const bmrInfo = generateBmrInfoToSendSidis(bmr, gender, info as any, {
      isMobile,
      positionType,
      articleCategories,
    })
    localStorage.setItem(BMR_SIDIS_KEY, JSON.stringify(bmrInfo))
    const redirectPageUrl = bmrLink
    const destination = generateBmrRedirect(
      `${
        typeof window !== 'undefined' ? window.location.origin : ''
      }${basePath}${redirectPageUrl}`,
      // bmr,
      gender,
      info as any,
    )
    router.push(
      {
        ...router,
        query: {
          ...router.query,
          gender: gender || '',
          birthday: age
            ? format(new Date(subYears(new Date(), age)), 'MM-dd-yyyy')
            : '',
        },
      },
      undefined,
      { shallow: true },
    )

    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(destination, 'bmr')
    } else {
      router.push(
        getURLwithSSOTracking(destination, { source: 'bmr' })?.href ||
          destination,
      )
    }
  }
  if (!bmrLink)
    return (
      <div
        style={{
          height: 500,
        }}
      ></div>
    )

  const bmiGenerated = BmiFormHelpers.generateBmiRules({ f, locale })
  const bmiUnit = BmiFormHelpers.bmiUnitValidation[locale]

  return onlyUseForm ? (
    <BmiForm
      onSubmit={onSubmit as any}
      bmiGenerated={bmiGenerated as any}
      bmiUnit={bmiUnit}
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
    />
  ) : (
    <BMRCardEmbed
      bmrContent={bmrContent}
      bmiGenerated={bmiGenerated as any}
      bmiUnit={bmiUnit}
      formProps={
        {
          formLayout: isMobile || isSide ? 'default' : 'inline',
          onSubmit,
          submitProps: {
            dataEventLabel: currentUrl,
          },
        } as BmiFormProps
      }
      {...props}
    />
  )
}
